import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { CopyableTextArea } from "@/components/podkit/forms/CopyableTextArea";
import { AWSRunnerAdditionalFieldsKeys } from "@/components/runners/details/runner-configuration-keys";
import { RunnerProvider } from "gitpod-next-api/gitpod/v1/runner_configuration_pb";
import type { PlainRunner } from "@/queries/runner-queries";
import { type FC } from "react";

type RunnerCleanupInstructionsProps = {
    runner: PlainRunner;
};

export const RunnerCleanupInstructions: FC<RunnerCleanupInstructionsProps> = ({ runner }) => {
    const stackURL = runner.status?.additionalInfo.find(
        (info) => info.key === AWSRunnerAdditionalFieldsKeys.StackURL,
    )?.value;

    // Don't show cleanup instructions for managed runners
    if (runner.provider === RunnerProvider.MANAGED) {
        return null;
    }

    return (
        <div className="space-y-3 rounded-md bg-surface-accent p-4">
            <Text className="font-bold">Important: Required cleanup steps</Text>

            {runner.provider === RunnerProvider.AWS_EC2 && (
                <>
                    {stackURL ? (
                        <>
                            <Text>
                                After clicking &quot;Yes, Delete&quot; below, delete the associated AWS CloudFormation
                                stack manually.
                            </Text>
                            <Text className="text-sm">
                                Stack URL:{" "}
                                <ExternalLink href={stackURL} className="break-all">
                                    {stackURL}
                                </ExternalLink>
                            </Text>
                        </>
                    ) : (
                        <Text>
                            After clicking &quot;Yes, Delete&quot; below, delete the associated AWS CloudFormation stack
                            manually (if it exists).
                        </Text>
                    )}
                </>
            )}

            {runner.provider === RunnerProvider.GCP && (
                <div className="flex flex-col gap-4">
                    <Text>Before deleting the runner from Ona, ensure all environments are deleted, then run:</Text>
                    <CopyableInput
                        className="w-full max-w-none font-mono text-sm"
                        copyable
                        value="terraform destroy"
                        data-tracking-id="copy-runner-cleanup-terraform"
                    />
                    <Text className="text-sm text-content-secondary">
                        This will clean up all GCP infrastructure created by Terraform.
                    </Text>
                </div>
            )}

            {runner.provider === RunnerProvider.LINUX_HOST && (
                <div className="flex flex-col gap-4">
                    <Text>After deleting the runner from Ona, complete these steps on your Linux machine:</Text>

                    <div className="flex flex-col gap-3">
                        <Text className="font-medium">1. Stop the runner process</Text>
                        <div className="text-sm text-content-secondary">If running as a systemd service:</div>
                        <CopyableInput
                            className="w-full max-w-none font-mono text-sm"
                            copyable
                            value="sudo systemctl stop gitpod-runner"
                            data-tracking-id="copy-stop-service-runner-cleanup-instructions"
                        />
                        <div className="text-sm text-content-secondary">
                            If running in the terminal, press Ctrl+C or terminate the <code>gitpod runner run</code>{" "}
                            process
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <Text className="font-medium">2. Remove runner files and environment data</Text>
                        <div className="text-sm text-content-secondary">
                            This will delete all runner files and environment data
                        </div>
                        <CopyableInput
                            className="w-full max-w-none font-mono text-sm"
                            copyable
                            value="rm -rf ~/gitpod-runner"
                            data-tracking-id="copy-remove-files-runner-cleanup-instructions"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Text className="font-medium">3. (Optional) Clean up service files</Text>
                        <div className="text-sm text-content-secondary">
                            If you set up a systemd service, you can remove it with:
                        </div>
                        <CopyableTextArea
                            className="w-full max-w-none font-mono text-sm"
                            copyable
                            value={`sudo systemctl disable gitpod-runner
sudo rm /etc/systemd/system/gitpod-runner.service`}
                            data-tracking-id="copy-remove-service-runner-cleanup-instructions"
                        />
                    </div>

                    <div className="flex flex-col gap-2">
                        <Text className="font-medium">4. Remove sudoers configuration</Text>
                        <div className="text-sm text-content-secondary">
                            Remove the sudoers configuration that was created during setup:
                        </div>
                        <CopyableInput
                            className="w-full max-w-none font-mono text-sm"
                            copyable
                            value="sudo rm /etc/sudoers.d/gitpod-runner"
                            data-tracking-id="copy-remove-sudoers-runner-cleanup-instructions"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};
